<template>
  <b-modal
    ref="reviewInfoModal"
    :title="$t('diagnosis.modals.reviewInformation')"
    centered
    modal-class="review-info-modal"
    hide-footer
  >
    <b-container v-if="revision">
      <b-row>
        <b-col class="form-area">
          <div class="px-3 py-3">
            <div class="pb-1">
              <span class="title">
                <template v-if="entityType === ENTITY_TYPE.SAMPLE">
                  <i
                    class="fal fa-prescription-bottle mr-2"
                    aria-hidden="true"
                  />
                  {{ $t('entities.sample.sample') }}
                </template>
                <template v-else-if="entityType === ENTITY_TYPE.PORTION">
                  <i
                    class="icon-block mr-2"
                    aria-hidden="true"
                  />
                  {{ $tc('global.block', 1) }}
                </template>
                <template v-else-if="entityType === ENTITY_TYPE.SUBPORTION">
                  <i
                    class="icon-porta mr-2"
                    aria-hidden="true"
                  />
                  {{ $tc('global.slide', 1) }}
                </template>
                {{ entityType === ENTITY_TYPE.PORTION ? entitySelected.portionLabel : entitySelected.label }}
              </span>
            </div>
            <div
              v-if="entityType === ENTITY_TYPE.SAMPLE"
              class="info-block mt-3"
            >
              <div class="form-label">
                <label>{{ $t('diagnosis.modals.sampleReviewType') }}</label>
              </div>
              <span class="dark">{{ revision.type.translatedName }}</span>
            </div>
            <div
              v-else-if="entityType === ENTITY_TYPE.PORTION"
              class="info-block mt-3"
            >
              <div class="form-label">
                <label>{{ $t('diagnosis.modals.blockReviewType') }}</label>
              </div>
              <span class="dark">{{ revision.type.translatedName }}</span>
            </div>
            <div
              v-else-if="entityType === ENTITY_TYPE.SUBPORTION"
              class="info-block mt-3"
            >
              <div class="form-label">
                <label>{{ $t('diagnosis.modals.slideReviewType') }}</label>
              </div>
              <span class="dark">{{ revision.type.translatedName }}</span>
            </div>
            <div>
              <hr>
            </div>
            <div
              v-if="revision.createdBy"
              class="info-block mt-3"
            >
              <div class="form-label">
                <label>{{ $t('diagnosis.modals.reviewPetitioner') }}</label>
              </div>
              <span class="dark">{{ revision.createdBy.fullName }}</span>
            </div>
            <div class="info-block mt-3">
              <div class="form-label">
                <label>{{ $t('diagnosis.modals.requestReviewDate') }}</label>
              </div>
              <span class="dark">{{ revision.creationDate | datetime }}</span>
            </div>
            <div
              v-if="revision.observations"
              class="info-block my-3"
            >
              <div class="form-label">
                <label>{{ $t('global.observations') }}</label>
              </div>
              <div class="observations-scroll-area">
                <span class="dark">{{ revision.observations }}</span>
              </div>
            </div>
          </div>
        </b-col>
        <b-col class="thumbs-area">
          <div>
            <p class="description">
              {{ $t('diagnosis.modals.picturesHelpTextShort') }}
            </p>
          </div>
          <div
            v-if="revision.revisionAttachments.length"
            class="scroll-area"
          >
            <vt-thumbnail
              v-for="(revisionAttachment) in revision.revisionAttachments"
              :key="revisionAttachment.attachment.attachmentId"
              ref="attachmentThumbnail"
              :laboratory-id="laboratoryId"
              :case-id="caseId"
              :sample-id="entitySelected.samId"
              :portion-id="entitySelected.porId"
              :subportion-id="entitySelected.subportionId"
              :attachment="revisionAttachment.attachment"
              @attachmentSelected="openAttachment($event)"
            />
          </div>
        </b-col>
      </b-row>
    </b-container>
    <div
      slot="modal-footer"
      class="footer-banner px-2"
      style="width:100%;"
    >
      <div class="row">
        <div class="col-6 text-left">
          <b-button
            size="sm"
            variant="outline-secondary"
            data-dismiss="modal"
            @click="hide"
          >
            {{ $t('global.cancel') }}
          </b-button>
        </div>
        <div class="col-6 text-right">
          <b-button
            size="sm"
            class="btn--primary"
            @click="hide"
          >
            <i
              class="fal fa-check mr-2"
              aria-hidden="true"
            />
            {{ $t('global.apply') }}
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import VtThumbnail from '@/components/common/VtThumbnail'
import { REVISION_ENTITY_TYPE } from '@/modules/diagnosisModule/constants/revisionEntityType'
import { ViewerType } from '@/modules/viewerModule/ViewerModule.models'
import { viewerService } from '@/modules/viewerModule/services/viewer.service'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/services/common/attachment/common-attachment.dto'
export default {
    name: 'ReviewInfoModal',
    components: {
        VtThumbnail
    },
    props: {
        laboratoryId: { type: Number },
        caseId: { type: Number }
    },
    data() {
        return {
            selected: null,
            revision: null,
            entitySelected: null,
            entityType: null
        }
    },
    computed: {
        ENTITY_TYPE() {
            return REVISION_ENTITY_TYPE
        },
        sampleId() {
            return this.entityType === REVISION_ENTITY_TYPE.SAMPLE ? this.entitySelected.samId : null
        },
        portionId() {
            return this.entityType === REVISION_ENTITY_TYPE.PORTION ? this.entitySelected.porId : null
        },
        subportionId() {
            return this.entityType === REVISION_ENTITY_TYPE.SUBPORTION ? this.entitySelected.subportionId : null
        },
        label() {
            return this.entityType === REVISION_ENTITY_TYPE.PORTION ? this.entitySelected.portionLabel : this.entitySelected.label
        }
    },
    methods: {
        show(revision, entitySelected, entityType) {
            this.revision = revision
            this.entitySelected = entitySelected
            this.entityType = entityType
            this.$refs.reviewInfoModal.show()
        },
        hide() {
            this.$refs.reviewInfoModal.hide()
            this.$_cleanComponentData()
        },
        async openAttachment(query) {
            if (query.attachmentEntityType === ATTACHMENT_ENTITY_TYPE.SAMPLE) {
                query.subportionId = null
            }
            this.openViewer(query, ViewerType.STATIC_VIEWER)
        },
        openViewer(query, type) {
            viewerService.openViewer({
                router: this.$router,
                viewerType: type,
                laboratoryId: query.laboratoryId,
                caseId: query.caseId,
                sampleId: query.sampleId,
                attachmentId: query.attachmentId,
                label: query.label,
                portionId: query.portionId,
                subportionId: query.subportionId
            })
        }
    }
}
</script>

<style lang="scss">
.review-info-modal .form-area {
    border-right: 1px solid vt-color(general-border);
}
.review-info-modal .form-area .title {
    color: vt-color(primary);
    font-size: font-size(very-normal);
}
.review-info-modal .modal-body {
    padding: 0;
    border-radius: .3rem;
}
.review-info-modal .info-block {
    line-height: 16px;
}
.review-info-modal .info-block span {
    font-size: font-size(very-small);
}
.review-info-modal .thumb-col {
    max-width: 100%;
}
.review-info-modal .thumb-check {
    display: none;
}
.review-info-modal .thumbs-area .scroll-area {
    height: calc(100vh - 550px);
    overflow-y: auto;
}
.review-info-modal .observations-scroll-area {
    height: 75px;
    overflow-y: auto;
}
.review-info-modal .thumbs-area .description {
    margin: 10px;
    font-size: font-size(very-small);
    line-height: 15px;
}
.review-info-modal .modal-dialog {
    max-width: 45% !important;
}
@media (max-width: 1300px) {
    .review-info-modal .modal-dialog {
        max-width: 55% !important;
    }
}
@media only screen and (max-width: 1366px) and (min-width: 1360px) {
    .review-info-modal .modal-dialog {
        max-width: 55% !important;
    }
    .review-info-modal .thumbs-area .scroll-area {
        height: calc(100vh - 300px);
    }
}
</style>
